// CS tool for an APPROVED order: remove items that went out of stock. Pick quantities to remove,
// preview what happens to the money (refund of the paid share via Airwallex, reduced balance), then
// confirm. The server refunds, updates the order + schedule, syncs Shopify and re-issues the invoice.
import { useRef, useState } from 'react';
import { previewAdjustment, applyAdjustment } from '../api/orders.js';
import { fmtMoney, sizeOf } from '../lib/matrix.js';

const errMsg = (e) => e.response?.data?.error?.message || 'Something went wrong.';

export default function OrderAdjustmentEditor({ order, onDone, onCancel }) {
  const currency = order.currency;
  const [remove, setRemove] = useState({}); // variantId → qty to remove (string)
  const [reason, setReason] = useState('OUT_OF_STOCK');
  const [note, setNote] = useState('');
  const [notifyCustomer, setNotifyCustomer] = useState(true);
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const inFlight = useRef(false); // money action — block a double-fire before `busy` re-renders

  const removals = Object.entries(remove)
    .map(([variantId, q]) => ({ variantId, quantity: Number(q) }))
    .filter((r) => Number.isInteger(r.quantity) && r.quantity > 0);

  const setQty = (variantId, v, max) => {
    const n = v === '' ? '' : String(Math.max(0, Math.min(max, Math.floor(Number(v) || 0))));
    setRemove((r) => ({ ...r, [variantId]: n }));
    setPlan(null); // any change invalidates the preview
  };

  const doPreview = async () => {
    setBusy('preview');
    setError('');
    try {
      setPlan(await previewAdjustment(order.id, { removals }));
    } catch (e) {
      setError(errMsg(e));
      setPlan(null);
    } finally {
      setBusy('');
    }
  };

  const doApply = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy('apply');
    setError('');
    try {
      const r = await applyAdjustment(order.id, { removals, reason, note: note.trim() || undefined, notifyCustomer });
      onDone(r);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      inFlight.current = false;
      setBusy('');
    }
  };

  return (
    <div className="rounded-lg border border-line bg-surface shadow-sm">
      <div className="border-b border-line px-4 py-3">
        <h3 className="text-sm font-semibold text-text-primary">Remove out-of-stock items</h3>
        <p className="mt-0.5 text-xs text-text-muted">
          Enter how many units to remove. Anything the client already paid for those items is refunded to their original payment
          method, the remaining balance is reduced, Shopify is updated, and a revised invoice is emailed.
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-line bg-surface-raised/60 text-left text-xs text-text-muted">
              <th className="px-4 py-2 font-medium">Style</th>
              <th className="px-4 py-2 font-medium">Size</th>
              <th className="px-4 py-2 text-right font-medium">Ordered</th>
              <th className="px-4 py-2 text-right font-medium">Unit</th>
              <th className="px-4 py-2 text-right font-medium">Remove</th>
            </tr>
          </thead>
          <tbody>
            {(order.lineItems || []).map((l) => {
              const v = remove[l.variantId] ?? '';
              const all = Number(v) === l.quantity;
              return (
                <tr key={l.variantId} className="border-b border-line last:border-0">
                  <td className="px-4 py-2 text-text-primary">
                    {l.title}
                    <div className="font-mono text-[11px] text-text-muted">{l.sku}</div>
                  </td>
                  <td className="px-4 py-2 text-text-secondary">{sizeOf(l)}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{l.quantity}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-text-secondary">{fmtMoney(l.unitPrice, currency)}</td>
                  <td className="px-4 py-2 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <input
                        type="number"
                        min={0}
                        max={l.quantity}
                        value={v}
                        onChange={(e) => setQty(l.variantId, e.target.value, l.quantity)}
                        placeholder="0"
                        aria-label={`Units of ${l.sku || l.title} to remove`}
                        className="w-16 rounded border border-line px-1.5 py-1 text-right text-xs tabular-nums"
                      />
                      <button
                        type="button"
                        onClick={() => setQty(l.variantId, all ? '' : l.quantity, l.quantity)}
                        className="text-[11px] text-text-muted hover:text-text-primary hover:underline"
                      >
                        {all ? 'clear' : 'all'}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="grid gap-2 border-t border-line px-4 py-3 sm:grid-cols-2">
        <label className="text-xs text-text-secondary">
          Reason
          <select value={reason} onChange={(e) => setReason(e.target.value)} className="mt-0.5 block w-full rounded border border-line bg-surface px-2 py-1 text-sm">
            <option value="OUT_OF_STOCK">Out of stock</option>
            <option value="OTHER">Other</option>
          </select>
        </label>
        <label className="text-xs text-text-secondary">
          Internal note (optional)
          <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} className="mt-0.5 block w-full rounded border border-line px-2 py-1 text-sm" />
        </label>
        <label className="flex items-center gap-2 text-xs text-text-secondary sm:col-span-2">
          <input type="checkbox" checked={notifyCustomer} onChange={(e) => setNotifyCustomer(e.target.checked)} className="accent-accent" />
          Email the client the revised invoice (explains the removed items and the refund)
        </label>
      </div>

      {plan && (
        <div className="mx-4 mb-3 rounded-md border border-line bg-surface-raised/40 p-3 text-xs">
          <div className="grid grid-cols-2 gap-y-1">
            <span className="text-text-muted">Order total</span>
            <span className="text-right tabular-nums">
              {fmtMoney(plan.oldPayable, currency)} → <strong>{fmtMoney(plan.newPayable, currency)}</strong>
            </span>
            <span className="text-text-muted">Refund to client</span>
            <span className={`text-right font-semibold tabular-nums ${plan.refundTotal > 0 ? 'text-error' : 'text-text-primary'}`}>
              {plan.refundTotal > 0 ? fmtMoney(plan.refundTotal, currency) : 'Nothing paid yet — no refund'}
            </span>
          </div>
          <ul className="mt-2 space-y-1 border-t border-line pt-2">
            {plan.installments.map((i) => (
              <li key={i.id} className="flex justify-between gap-2">
                <span className="text-text-secondary">
                  {i.label} <span className="text-text-muted">· {i.status === 'PAID' ? 'paid' : 'unpaid'}</span>
                </span>
                <span className="tabular-nums">
                  {fmtMoney(i.oldAmount, currency)} → {fmtMoney(i.newAmount, currency)}
                  {i.refund > 0 && <span className="text-error"> · refund {fmtMoney(i.refund, currency)}</span>}
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-2 text-text-muted">
            {plan.shopify === 'refund_order'
              ? 'Shopify: the removed lines are refunded on the Shopify order and taken off fulfilment.'
              : plan.shopify === 'update_draft'
                ? 'Shopify: the draft order’s lines are updated.'
                : 'Shopify: no Shopify order linked — platform only.'}
          </p>
        </div>
      )}

      {error && <p className="mx-4 mb-3 text-xs text-error">{error}</p>}

      <div className="flex flex-wrap justify-end gap-2 border-t border-line px-4 py-3">
        <button onClick={onCancel} className="rounded-md border border-line px-3 py-1.5 text-sm text-text-secondary hover:bg-surface-raised">
          Cancel
        </button>
        {!plan ? (
          <button
            onClick={doPreview}
            disabled={!removals.length || busy === 'preview'}
            className="rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-white hover:bg-accent-hover disabled:opacity-50"
          >
            {busy === 'preview' ? 'Calculating…' : 'Preview refund'}
          </button>
        ) : (
          <button
            onClick={doApply}
            disabled={busy === 'apply'}
            className="rounded-md bg-error px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
          >
            {busy === 'apply'
              ? 'Processing…'
              : plan.refundTotal > 0
                ? `Remove items & refund ${fmtMoney(plan.refundTotal, currency)}`
                : 'Remove items'}
          </button>
        )}
      </div>
    </div>
  );
}

/** Read-only history of past adjustments on an order (all roles that can see the order). */
export function AdjustmentHistory({ adjustments, currency, isStaff }) {
  if (!adjustments?.length) return null;
  return (
    <div className="rounded-lg border border-line bg-surface p-4 shadow-sm">
      <h3 className="mb-2 text-sm font-semibold text-text-primary">Adjustments</h3>
      <ul className="space-y-3">
        {adjustments.map((a) => (
          <li key={a.id} className="text-xs">
            <div className="flex justify-between gap-2">
              <span className="font-medium text-text-primary">
                {a.reason === 'OUT_OF_STOCK' ? 'Removed — out of stock' : 'Removed'}
              </span>
              <span className="text-text-muted">{new Date(a.createdAt).toLocaleDateString()}</span>
            </div>
            <ul className="mt-1 text-text-secondary">
              {(a.removedLines || []).map((l, i) => (
                <li key={i}>
                  {l.quantity} × {l.title} {l.sku && <span className="font-mono text-[10px] text-text-muted">{l.sku}</span>}
                </li>
              ))}
            </ul>
            <div className="mt-1 text-text-muted">
              Total {fmtMoney(a.oldPayable, currency)} → {fmtMoney(a.newPayable, currency)}
              {a.refundTotal > 0 && <> · refunded {fmtMoney(a.refundTotal, currency)}</>}
            </div>
            {isStaff && (a.status === 'PARTIAL' || a.refunds?.some((r) => r.status === 'FAILED')) && (
              <p className="mt-1 rounded border border-warning/40 bg-warning-light px-2 py-1 text-text-primary">
                Needs follow-up:{' '}
                {a.refunds?.some((r) => r.status === 'FAILED')
                  ? 'a refund failed — issue it in the Airwallex dashboard.'
                  : `Shopify wasn’t updated${a.shopifyError ? ` (${a.shopifyError})` : ''} — refund the removed lines on the Shopify order.`}
              </p>
            )}
            {isStaff && a.createdByEmail && <div className="text-[11px] text-text-muted">by {a.createdByEmail}</div>}
          </li>
        ))}
      </ul>
    </div>
  );
}
